import {
  getOffsetTopRelativeTo,
  getScrollableAncestor,
  isElementInView,
} from "@prismicio/editor-support/DOM"
import { useInView, useOnChange } from "@prismicio/editor-support/React"
import {
  type ReactNode,
  type RefObject,
  useLayoutEffect,
  useRef,
  useState,
} from "react"
import { useHotkeys } from "react-hotkeys-hook"

import { type ThemeKeys } from "../../theme"
import { Box } from "../Box/Box"

/**
 * Lazily renders a child the first time it intersects with the viewport.
 * Works with any vertical scrolling ancestor (direct or not).
 * Note: For now, it's probably safer not to nest LazyRender components.
 */

export interface LazyRenderProps {
  /**
   * The children to display when LazyRender intersects with the viewport.
   */
  children: ReactNode

  /**
   * A rough estimate of the children's height so that not too many or too few LazyRender components are visible at once.
   * Getting this right is not critical.
   * A heightEstimate of 0 means nothing will lazily render as everything is immediately visible.
   * On the other hand, a huge heightEstimate means it will be hard to see and load the content after the first item.
   * @default 200
   */
  heightEstimate?: number

  /**
   * Whether to keep the children loaded forever after they were shown the first time.
   * Otherwise, will keep mounting/unmounting the children as they appear on screen.
   * @default false
   */
  keepLoaded?: boolean

  /**
   * Whether to immediately load when the user is about to initiate a native page text search.
   */
  loadWhenSearching?: boolean

  gap?: ThemeKeys<"space">
}

export function LazyRender(props: LazyRenderProps) {
  const {
    children,
    heightEstimate = 200,
    keepLoaded = false,
    loadWhenSearching = false,
    gap,
  } = props

  const ref = useRef<HTMLElement>(null)

  const { show, setShow } = useShowElement({ ref, keepLoaded })

  useHotkeys("mod+F", () => {
    if (!loadWhenSearching) return
    setShow(true)
  })

  useOffsetScroll({ ref, show })

  return (
    <Box
      ref={ref}
      flexDirection="column"
      height={show ? "auto" : heightEstimate}
      gap={gap}
    >
      {show && children}
    </Box>
  )
}

interface UseShowElementArgs {
  ref: RefObject<HTMLElement | null>
  keepLoaded: boolean
}

function useShowElement(args: UseShowElementArgs) {
  const { ref, keepLoaded } = args

  const inView = useInView(ref, { once: keepLoaded })
  const [show, setShow] = useState(inView)

  useLayoutEffect(() => {
    // Always show immediately.
    if (inView) return setShow(true)

    if (!ref.current) return
    if (keepLoaded) return

    // Debounce the hiding of the element.
    // This is to fix a bug with IntersectionObserver being confused and reporting a hide when playing an animation:
    // Thus, we check again ourselves a bit later, synchronously.
    // We also wait till the user finished scrolling for some time, so as to keep a smooth scroll (especially on safari)
    let scrollTimeout: number | undefined

    const scroller = getScrollableAncestor(ref.current)
    let lastScrollTop = scroller.scrollTop

    function checkLater() {
      scrollTimeout = window.setTimeout(() => {
        if (!ref.current) return
        if (isElementInView(ref.current)) return

        // Didn't scroll in the meantime: Now's a good time to hide.
        if (lastScrollTop == scroller.scrollTop) return setShow(false)

        lastScrollTop = scroller.scrollTop
        checkLater()
      }, 2_000)
    }

    checkLater()

    return () => clearTimeout(scrollTimeout)
  }, [inView, ref, keepLoaded])

  return { show, setShow }
}

interface UseOffsetScrollParams {
  ref: RefObject<HTMLElement | null>
  show: boolean
}

// When LazyRender renders its children, offset the current scroll position so that it looks stable.
// The bad jumping behavior (when not using this hook) is mostly noticeable when scrolling up.
function useOffsetScroll(params: UseOffsetScrollParams) {
  const { ref, show } = params

  const [offsetScroll, setOffsetScroll] = useState({ previousHeight: 0 })

  useOnChange(show, () => {
    const previousHeight = ref.current?.clientHeight
    if (!previousHeight) return

    // Remember the current height as it's going to change in the next layout phase.
    setOffsetScroll({ previousHeight })
  })

  useLayoutEffect(() => {
    // Ignore at mount time: Nothing to offset because if we're already visible, the children are rendered immediately.
    if (offsetScroll.previousHeight === 0) return
    if (!ref.current) return

    const scrollParent = getScrollableAncestor(ref.current)
    if (!scrollParent) return

    const elementTop = getOffsetTopRelativeTo(ref.current, scrollParent)

    // The element is below the scroll top, it won't affect the scroll position when it renders so ignore.
    if (elementTop >= scrollParent.scrollTop) return

    const newHeight = ref.current.clientHeight
    const heightDifference = newHeight - offsetScroll.previousHeight

    scrollParent.scrollTop += heightDifference
  }, [offsetScroll, ref])
}
